import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Loader2, Save, Trash2, Upload, UploadCloud } from 'lucide-react';
import { apiService, PackEntityDTO, PackGeometryRef } from '@/services/api.service';
import { clearPackTextureCache } from '@/services/packAssets';
import { useUnsavedDraft } from '@/stores/unsavedDrafts.store';
import { PackThumbnail } from './PackThumbnail';
import { SearchPicker } from './SearchPicker';
import styles from './PackForm.module.scss';

type EntityTab = 'info' | 'behavior';
const TABS: { id: EntityTab; label: string }[] = [
  { id: 'info', label: 'Information' },
  { id: 'behavior', label: 'Comportement' },
];

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
function readAsText(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsText(file);
  });
}

interface EntityEditorProps {
  entity: PackEntityDTO;
  groupId: string;
  onSaved?: () => void;
  readonlyResource?: boolean;
  collisionWidth?: number | null;
  collisionHeight?: number | null;
  /** Aperçu 3D : sélection courante + fichiers en attente. */
  onPreviewOverride?: (o: {
    textureUrl: string | null;
    texturePath: string | null;
    geometryText: string | null;
    geometryFile: string | null;
    geometryId: string;
  }) => void;
}

export function EntityEditor({ entity, groupId, onSaved, readonlyResource, collisionWidth, collisionHeight, onPreviewOverride }: EntityEditorProps) {
  const primaryTexture = Object.values(entity.textures || {})[0] || null;

  const [nameEn, setNameEn] = useState('');
  const [nameFr, setNameFr] = useState('');
  const [geometry, setGeometry] = useState(entity.geometry || '');
  const [spawnable, setSpawnable] = useState(false);
  const [summonable, setSummonable] = useState(false);
  const [scale, setScale] = useState('');
  const [tags, setTags] = useState('');
  const [health, setHealth] = useState('');
  const [hasCollision, setHasCollision] = useState(true);
  const [hasGravity, setHasGravity] = useState(true);
  const [fireImmune, setFireImmune] = useState(false);
  const [movement, setMovement] = useState('');
  const [timerLooping, setTimerLooping] = useState(false);
  const [timerTime, setTimerTime] = useState('');

  const [geometries, setGeometries] = useState<PackGeometryRef[]>([]);
  const [stagedTex, setStagedTex] = useState<File | null>(null);
  const [stagedGeo, setStagedGeo] = useState<File | null>(null);

  const [tab, setTab] = useState<EntityTab>('info');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const texFileRef = useRef<HTMLInputElement>(null);
  const geoFileRef = useRef<HTMLInputElement>(null);
  const stagedTexUrl = useMemo(() => (stagedTex ? URL.createObjectURL(stagedTex) : null), [stagedTex]);
  useEffect(() => () => { if (stagedTexUrl) URL.revokeObjectURL(stagedTexUrl); }, [stagedTexUrl]);

  // Reflète l'état courant dans l'aperçu 3D du parent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const geometryText = stagedGeo ? await readAsText(stagedGeo) : null;
      if (cancelled) return;
      const geometryFile = stagedGeo ? null : (geometries.find((g) => g.id === geometry)?.file ?? entity.geometryFile);
      onPreviewOverride?.({
        textureUrl: stagedTexUrl,
        texturePath: stagedTex ? null : primaryTexture,
        geometryText,
        geometryFile,
        geometryId: geometry,
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedTexUrl, stagedGeo, geometry, geometries]);

  useEffect(() => {
    apiService.getPackGeometries(groupId).then((r) => setGeometries(r.data?.geometries ?? []));
    apiService.getPackLang(groupId, `entity.${entity.identifier}.name`).then((r) => {
      setNameEn(r.data?.translations?.en_US ?? '');
      setNameFr(r.data?.translations?.fr_FR ?? '');
    });
    if (entity.source) {
      apiService.getPackFileJson(groupId, entity.source).then((r) => {
        const e = (r.data as any)?.['minecraft:entity'];
        const d = e?.description ?? {};
        const c = e?.components ?? {};
        setSpawnable(!!d.is_spawnable);
        setSummonable(!!d.is_summonable);
        const h = c['minecraft:health'];
        const hv = typeof h === 'object' ? h?.value : h;
        if (hv != null) setHealth(String(hv));
        const p = c['minecraft:physics'];
        if (p) { setHasCollision(p.has_collision !== false); setHasGravity(p.has_gravity !== false); }
        setFireImmune(!!c['minecraft:fire_immune']);
        const mv = c['minecraft:movement'];
        const mvv = typeof mv === 'object' ? mv?.value : mv;
        if (mvv != null) setMovement(String(mvv));
        const sc = c['minecraft:scale'];
        const scv = typeof sc === 'object' ? sc?.value : sc;
        if (scv != null) setScale(String(scv));
        const tg = c['minecraft:tags']?.tags;
        if (Array.isArray(tg)) setTags(tg.join(', '));
        const tm = c['minecraft:timer'];
        if (tm) { setTimerLooping(!!tm.looping); if (tm.time != null) setTimerTime(String(tm.time)); }
      });
    }
  }, [groupId, entity.identifier, entity.source]);

  const num = (v: string) => (v === '' ? null : Number(v));

  const save = async (): Promise<boolean> => {
    setSaving(true);
    setFeedback(null);

    // 1) Intègre les fichiers en attente (upload).
    let geo = geometry;
    let tex: string | null = null; // seulement si une nouvelle texture est uploadée
    try {
      if (stagedTex) {
        const r = await apiService.importPackTexture(groupId, {
          key: stagedTex.name.replace(/\.[^.]+$/, ''),
          fileName: stagedTex.name,
          fileData: await readAsDataUrl(stagedTex),
          atlas: 'entity',
        });
        if (!r.data?.success || !r.data.value) { setSaving(false); setFeedback({ ok: false, msg: r.data?.message || r.error || 'Échec import texture' }); return false; }
        tex = r.data.value;
      }
      if (stagedGeo) {
        const r = await apiService.importPackGeometry(groupId, await readAsText(stagedGeo));
        if (!r.data?.success || !r.data.value) { setSaving(false); setFeedback({ ok: false, msg: r.data?.message || r.error || 'Échec import géométrie' }); return false; }
        geo = r.data.value;
      }
    } catch {
      setSaving(false);
      setFeedback({ ok: false, msg: "Échec de l'intégration des fichiers" });
      return false;
    }

    // 2) Enregistre l'entité.
    const res = await apiService.saveEntityForm(groupId, {
      identifier: entity.identifier,
      source: entity.source,
      rpSource: entity.rpSource,
      geometry: geo || null,
      texture: tex,
      translations: { en_US: nameEn, fr_FR: nameFr },
      spawnable,
      summonable,
      health: num(health),
      collisionWidth: collisionWidth ?? null,
      collisionHeight: collisionHeight ?? null,
      hasCollision,
      hasGravity,
      fireImmune,
      movement: num(movement),
      scale: num(scale),
      tags: tags.split(/[,\n]/).map((t) => t.trim()).filter(Boolean),
      timerLooping,
      timerTime: num(timerTime),
    });
    setSaving(false);
    if (res.data?.success) {
      setGeometry(geo);
      setStagedTex(null);
      setStagedGeo(null);
      if (stagedTex) clearPackTextureCache(groupId);
      if (stagedGeo) apiService.getPackGeometries(groupId).then((r) => setGeometries(r.data?.geometries ?? []));
      setDirty(false);
      setFeedback({ ok: true, msg: res.data.message });
      onSaved?.();
      return true;
    }
    setFeedback({ ok: false, msg: res.data?.message || res.error || 'Échec' });
    return false;
  };

  // Suivi des modifications non enregistrées → barre « Enregistrer tout ».
  useUnsavedDraft({ kind: 'entity', groupId, identifier: entity.identifier, label: entity.identifier, dirty, save });

  const publish = async () => {
    setPublishing(true);
    setFeedback(null);
    const res = await apiService.publishPackGroup(groupId);
    setPublishing(false);
    setFeedback(res.data ? { ok: res.data.success, msg: res.data.messages.join(' · ') } : { ok: false, msg: res.error || 'Échec publication' });
  };

  const geoOptions = geometries.map((g) => ({ value: g.id, label: g.id.replace(/^geometry\./, ''), hint: g.id }));

  return (
    <div className={styles.form} onChange={() => setDirty(true)} onInput={() => setDirty(true)}>
      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.id} type="button" className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`} onClick={() => { setTab(t.id); setFeedback(null); }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <>
          {readonlyResource && (
            <div className={styles.ok}>Resource pack en lecture seule (vanilla) — seul le <strong>comportement</strong> est éditable.</div>
          )}

          <div className={styles.field}>
            <label className={styles.label}>Identifiant</label>
            <input className={styles.input} value={entity.identifier} readOnly disabled />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Géométrie</label>
            {stagedGeo ? (
              <div className={styles.stagedFile}>
                <span className={styles.stagedName}>{stagedGeo.name}</span>
                <span className={styles.stagedTag}>à intégrer</span>
                <button type="button" className={styles.stagedDel} onClick={() => setStagedGeo(null)} title="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <div className={styles.pickerRow}>
                <div className={styles.pickerGrow}>
                  <SearchPicker groupId={groupId} value={geometry} onChange={(v) => { setGeometry(v); setDirty(true); }} options={geoOptions} placeholder="geometry.…" />
                </div>
                <button className={styles.uploadBtn} disabled={readonlyResource} onClick={() => geoFileRef.current?.click()}>
                  <Upload size={14} /> Importer
                  <input ref={geoFileRef} type="file" accept=".json,.geo.json,application/json" onChange={(e) => e.target.files?.[0] && setStagedGeo(e.target.files[0])} />
                </button>
              </div>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Texture</label>
            {stagedTex ? (
              <div className={styles.stagedFile}>
                {stagedTexUrl && <img className={styles.stagedThumb} src={stagedTexUrl} alt="" />}
                <span className={styles.stagedName}>{stagedTex.name}</span>
                <span className={styles.stagedTag}>à intégrer</span>
                <button type="button" className={styles.stagedDel} onClick={() => setStagedTex(null)} title="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <div className={styles.pickerRow}>
                {primaryTexture && <PackThumbnail groupId={groupId} path={primaryTexture} size={36} />}
                <input className={`${styles.input} ${styles.pickerGrow}`} value={primaryTexture ? primaryTexture.split('/').pop() || '' : 'aucune'} readOnly disabled />
                {!readonlyResource && (
                  <button className={styles.uploadBtn} onClick={() => texFileRef.current?.click()}>
                    <Upload size={14} /> Importer
                    <input ref={texFileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && setStagedTex(e.target.files[0])} />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className={styles.row2}>
            <label className={styles.checkRow}>
              <input type="checkbox" checked={spawnable} onChange={(e) => setSpawnable(e.target.checked)} />
              <span>Apparaît naturellement (is_spawnable)</span>
            </label>
            <label className={styles.checkRow}>
              <input type="checkbox" checked={summonable} onChange={(e) => setSummonable(e.target.checked)} />
              <span>Invocable (is_summonable)</span>
            </label>
          </div>

          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Taille (scale)</label>
              <input className={styles.input} type="number" min={0} step={0.1} value={scale} onChange={(e) => setScale(e.target.value)} placeholder="inchangée" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Tags (séparés par des virgules)</label>
              <input className={styles.input} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="ex. nolagg" />
            </div>
          </div>

          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Nom affiché (EN)</label>
              <input className={styles.input} value={nameEn} onChange={(e) => setNameEn(e.target.value)} disabled={readonlyResource} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Nom affiché (FR)</label>
              <input className={styles.input} value={nameFr} onChange={(e) => setNameFr(e.target.value)} disabled={readonlyResource} />
            </div>
          </div>
        </>
      )}

      {tab === 'behavior' && (
        <>
          <div className={styles.row2}>
            <div className={styles.field}>
              <label className={styles.label}>Points de vie (health)</label>
              <input className={styles.input} type="number" min={0} value={health} onChange={(e) => setHealth(e.target.value)} placeholder="ex. 20" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Vitesse de déplacement (movement)</label>
              <input className={styles.input} type="number" min={0} step={0.01} value={movement} onChange={(e) => setMovement(e.target.value)} placeholder="ex. 0.25" />
            </div>
          </div>

          <label className={styles.checkRow}>
            <input type="checkbox" checked={hasCollision} onChange={(e) => setHasCollision(e.target.checked)} />
            <span>A une collision physique (has_collision)</span>
          </label>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={hasGravity} onChange={(e) => setHasGravity(e.target.checked)} />
            <span>Soumise à la gravité (has_gravity)</span>
          </label>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={fireImmune} onChange={(e) => setFireImmune(e.target.checked)} />
            <span>Immunisée au feu (fire_immune)</span>
          </label>

          <div className={styles.row2}>
            <label className={styles.checkRow}>
              <input type="checkbox" checked={timerLooping} onChange={(e) => setTimerLooping(e.target.checked)} />
              <span>Minuteur en boucle (timer.looping)</span>
            </label>
            <div className={styles.field}>
              <label className={styles.label}>Minuteur — durée (s)</label>
              <input className={styles.input} type="number" min={0} step={0.1} value={timerTime} onChange={(e) => setTimerTime(e.target.value)} placeholder="ex. 5" />
            </div>
          </div>
        </>
      )}

      {feedback && (
        <div className={feedback.ok ? styles.ok : styles.error}>
          {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
        </div>
      )}

      <div className={styles.actions}>
        <button className={styles.saveBtn} onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button className={styles.publishBtn} onClick={publish} disabled={publishing}>
          {publishing ? <Loader2 size={14} className={styles.spin} /> : <UploadCloud size={14} />}
          {publishing ? 'Publication…' : 'Publier sur git'}
        </button>
      </div>
    </div>
  );
}
